// .../podtemat2_permutacje_types/CinemaAdjacentPairTask.js
// Permutacje bez powtórzeń — "kino":
// Wariant losowy: k = 2 (AB), k = 3 (ABG), k = 4 (ABGD)
// Wzór ogólny: k! · (n - k + 1)!
// Zakres: n ∈ {6..10}
//
// Wersja z odpornością na stare taski (mogą nie mieć pól k/names).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

// helpers
function randInt(a, b) { return Math.floor(Math.random() * (b - a + 1)) + a; }
function fact(n) { let r = 1; for (let i = 2; i <= n; i++) r *= i; return r; }

// --- robust helpers (fallbacks for legacy tasks) ---
function inferK(n, answer) {
  // próbujemy dopasować do 2!·(n-1)!, 3!·(n-2)!, 4!·(n-3)!
  try {
    if (n >= 2 && answer % (2 * fact(n - 1)) === 0) return 2;
    if (n >= 3 && answer % (6 * fact(n - 2)) === 0) return 3;
    if (n >= 4 && answer % (24 * fact(n - 3)) === 0) return 4;
  } catch {}
  return 2; // domyślka
}
function ensureK(data) {
  if (typeof data.k === "number") return data.k;
  return inferK(data.n, data.answer ?? 0);
}
function ensureNames(k, data) {
  if (Array.isArray(data.names) && data.names.length >= k) return data.names;
  if (k === 4) return ["Ala", "Bartek", "Gosia", "Dawid"];
  if (k === 3) return ["Ala", "Bartek", "Gosia"];
  return ["Ala", "Bartek"];
}

export function generateTask() {
  const n = randInt(6, 10);           // nowy zakres 6..10
  const ks = [2, 3, 4];               // losujemy rozmiar bloku
  const k = ks[Math.floor(Math.random() * ks.length)];
  const names = ensureNames(k, {});   // zgodnie z k

  // ogólny wzór: k! · (n - k + 1)!
  const answer = fact(k) * fact(n - k + 1);

  return { type: "CinemaAdjacentPairTask", data: { n, k, names, answer } };
}

export function renderTask({ data }) {
  const n = data.n;
  const k = ensureK(data);
  const names = ensureNames(k, data);

  let who;
  if (k === 2) who = <><strong>{names[0]}</strong> i <strong>{names[1]}</strong></>;
  else if (k === 3) who = <><strong>{names[0]}</strong>, <strong>{names[1]}</strong> i <strong>{names[2]}</strong></>;
  else who = <><strong>{names[0]}</strong>, <strong>{names[1]}</strong>, <strong>{names[2]}</strong> i <strong>{names[3]}</strong></>;

  return (
    <div style={{ textAlign: "left" }}>
      <p>
        W jednym rzędzie kina jest <InlineMath math={String(n)} /> miejsc i tyle samo różnych osób.
        Wśród nich są {who}. Na ile sposobów można ich usadzić, jeśli
        {" "}{k === 2 ? "muszą siedzieć obok siebie" : k === 3 ? "wszyscy troje muszą siedzieć obok siebie" : "wszyscy czworo muszą siedzieć obok siebie"}?
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label=""
      placeholder="Wpisz liczbę"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return { correct: false, message: "Podaj liczbę." };
  const given = parseInt(userAnswer.raw, 10);
  if (Number.isNaN(given)) return { correct: false, message: "Podaj liczbę całkowitą." };
  const correct = given === data.answer;
  userAnswer.correctAnswer = data.answer;
  return { correct, message: correct ? "Dobrze!" : "Spróbuj ponownie." };
}

export function renderAnswer({ data }) {
  return <InlineMath math={String(data.answer)} />;
}

export function renderUserAnswer(userAnswer) {
  return (!userAnswer || !userAnswer.raw)
    ? <i>(brak odpowiedzi)</i>
    : <InlineMath math={String(userAnswer.raw)} />;
}

export function renderExplanation({ data }) {
  const n = data.n;
  const k = ensureK(data);      // 2, 3 lub 4
  const m = n - k + 1;          // liczba elementów po zblokowaniu
  const answer = data.answer;

  // wartości liczbowe silni
  const kFact = fact(k);
  const mFact = fact(m);

  const label =
    k === 2 ? "blok pary (AB)" :
    k === 3 ? "blok trójki (ABG)" :
              "blok czwórki (ABGD)";

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1 (blok).</strong> Traktujemy {label} jako jeden element.
        Z początkowych <InlineMath math={String(n)} /> osób otrzymujemy
        liczbę elementów do ustawienia równą <InlineMath math={'n - k + 1'} />:
      </p>

      <ul style={{ marginTop: 8 }}>
        <li><InlineMath math={'-\\,k'} /> — bo <InlineMath math={'k'} /> osób scalamy w jeden blok,</li>
        <li><InlineMath math={'+\\,1'} /> — bo sam blok liczy się jako <em>jeden</em> element.</li>
      </ul>

      <BlockMath math={`${n} - ${k} + 1 = ${m}`} />

      <p><strong>Krok 2 (permutacje elementów).</strong> Ustawiamy te elementy na miejscach:</p>
      <BlockMath math={`(${n} - ${k} + 1)! = ${m}! = ${mFact}`} />

      <p><strong>Krok 3 (kolejność w bloku).</strong> Możliwe ustawienia w środku bloku:</p>
      <BlockMath math={`${k}! = ${kFact}`} />

      <p><strong>Wynik łączny.</strong></p>
      <BlockMath math={`${k}!\\cdot(${n}-${k}+1)! = ${k}!\\cdot${m}! = ${kFact}\\cdot${mFact} = ${answer}`} />
    </div>
  );
}
